import type { PartsAuthenticityMap } from './partsAuthenticity'
import type { SellerPublicSummary } from './sellerProfile'
import type { ListingTrustInput } from './trust'
import type { DemoListing } from './demoListings'

export type DemoListingDetailView = ListingTrustInput & {
  demoId: string
  title: string
  description: string
  phone_model: {
    manufacturer: string
    model_name: string
    release_year?: number | null
    description?: string | null
    specs_json?: string | null
  }
  seller: SellerPublicSummary
  seller_city: string
  battery_cycles?: number | null
  battery_original?: boolean | null
  battery_tested_at?: string | null
  gradient: string
  glyph: string
  usage_duration?: string | null
}

function demoNumericId(id: string): number {
  const n = parseInt(id.replace(/\D/g, ''), 10)
  return Number.isFinite(n) ? n : 1
}

function isApple(manufacturer: string): boolean {
  return /apple/i.test(manufacturer)
}

function isShopSeller(name: string): boolean {
  return /mb|shop|mart|baltic|premium|retro|foto|elektronika/i.test(name)
}

function buildDemoSeller(demo: DemoListing): SellerPublicSummary {
  const n = demoNumericId(demo.id)
  const shop = isShopSeller(demo.seller)
  const verified = shop || n % 3 === 0
  return {
    display_name: demo.seller,
    verification_level: shop ? 2 : verified ? 2 : 1,
    member_since: `202${4 + (n % 3)}-0${1 + (n % 8)}-12T00:00:00Z`,
    completed_deals_count: shop ? 8 + n : n % 6,
    rating_avg: n % 5 === 0 ? null : Math.min(5, 4.1 + (n % 9) * 0.1),
    rating_count: n % 5 === 0 ? 0 : 2 + (n % 12),
    is_verified_seller: verified,
  }
}

function buildDemoParts(demo: DemoListing): PartsAuthenticityMap {
  const n = demoNumericId(demo.id)
  const apple = isApple(demo.manufacturer)

  if (demo.condition === 'damaged') {
    return {
      display: { status: 'replaced_aftermarket', grade: 'incell' },
      camera: { status: 'original' },
      housing: { status: 'unknown' },
      back_glass: { status: n % 2 === 0 ? 'replaced' : 'unknown' },
    }
  }

  if (demo.condition === 'fair') {
    return {
      display: { status: 'unknown' },
      housing: { status: 'unknown' },
      back_glass: { status: 'unknown' },
    }
  }

  const parts: PartsAuthenticityMap = {
    display: { status: demo.condition === 'excellent' ? 'original' : 'unknown' },
    camera: { status: 'original' },
    housing: { status: demo.condition === 'excellent' ? 'original' : 'unknown' },
    back_glass: { status: 'unknown' },
  }

  if (apple && !/\bse\b/i.test(demo.model)) {
    parts.face_id = { status: demo.condition === 'excellent' ? 'original' : 'unknown' }
  }

  return parts
}

function buildDemoMarketPrice(price: number, id: string): number {
  const n = demoNumericId(id)
  const mult = 1.28 + (n % 7) * 0.06
  return Math.round(price * mult)
}

function buildInBox(demo: DemoListing): string {
  const n = demoNumericId(demo.id)
  if (demo.condition === 'new_sealed' || demo.condition === 'unused') return 'full'
  if (n % 3 === 0) return 'partial'
  if (n % 5 === 0) return 'phone_only'
  return 'full'
}

function demoImeiDigits(masked: string): string {
  const digits = masked.replace(/\D/g, '')
  return digits.length >= 4 ? `0000000000${digits}`.slice(-15) : '000000000000000'
}

export function buildDemoListingDetail(demo: DemoListing): DemoListingDetailView {
  const n = demoNumericId(demo.id)
  const apple = isApple(demo.manufacturer)
  const battery = demo.battery ?? null

  return {
    demoId: demo.id,
    id: n,
    title: demo.title,
    description: demo.description,
    price: demo.price,
    condition: demo.condition,
    phone_model: {
      manufacturer: demo.manufacturer,
      model_name: demo.model,
      release_year: demo.releaseYear ?? null,
    },
    seller: buildDemoSeller(demo),
    seller_city: demo.location,
    battery_health: battery,
    battery_cycles: battery != null && n % 4 === 0 ? 180 + n * 12 : null,
    battery_original: battery != null ? apple || n % 4 !== 2 : null,
    battery_tested_at: battery != null ? `2026-0${(n % 6) + 1}-15T10:00:00Z` : null,
    parts_authenticity: buildDemoParts(demo),
    imei: demoImeiDigits(demo.imeiMasked),
    imei_verified: n % 4 === 1,
    ai_recommended_eur: buildDemoMarketPrice(demo.price, demo.id),
    created_at: `2026-0${(n % 6) + 1}-${String(8 + (n % 18)).padStart(2, '0')}T${10 + (n % 8)}:22:00Z`,
    in_box_completeness: buildInBox(demo),
    has_factory_warranty: demo.price >= 650 && n % 3 === 0,
    warranty_remaining: demo.price >= 650 && n % 3 === 0 ? '6_months' : null,
    shipment_method: 'peer',
    usage_duration: n % 3 === 0 ? '1_2_years' : n % 3 === 1 ? '6_12_months' : 'under_6_months',
    gradient: demo.gradient,
    glyph: demo.glyph,
  }
}

export function demoListingHeadline(
  detail: Pick<DemoListingDetailView, 'title' | 'phone_model'>,
): { brand: string; title: string } {
  const manufacturer = detail.phone_model.manufacturer.trim()
  const modelName = detail.phone_model.model_name.trim()
  const modelLine = `${manufacturer} ${modelName}`.trim()
  const title = detail.title.trim()
  const tLower = title.toLowerCase()
  const mLower = modelLine.toLowerCase()

  if (!title || tLower === mLower) {
    return { brand: manufacturer, title: modelName }
  }
  if (tLower.startsWith(mLower)) {
    return { brand: manufacturer, title }
  }
  return { brand: manufacturer, title }
}

export function demoListingImeiDisplay(masked: string): string {
  const digits = masked.replace(/\D/g, '')
  if (digits.length >= 4) return `…${digits.slice(-4)}`
  return masked || '—'
}
